import type { ComponentChildren } from 'preact';

import { cx, Icon, type IconId } from '@amumin/ui-core';

import styles from './Button.module.css';

export interface ButtonProps {
  children?: ComponentChildren;
  /** Stabiele naam (data-name) voor tests en logging. */
  name: string;
  onPress?: () => void;
  /** `submit` laat Enter in een formulier werken; standaard `button`. */
  type?: 'button' | 'submit';
  /**
   * primary = de ene hoofdactie van het scherm; default/secondary = rustig omlijnd;
   * ghost = zonder rand (werkbalken); danger = verwijderen e.d.; link = tekstlink.
   */
  variant?: 'default' | 'secondary' | 'primary' | 'ghost' | 'danger' | 'link';
  size?: 'small' | 'normal' | 'large';
  /** Icoon links van de tekst (of alleen icoon: geef dan `label`). */
  icon?: IconId;
  /** Toegankelijke naam voor een knop met alleen een icoon. */
  label?: string;
  disabled?: boolean;
  busy?: boolean;
  block?: boolean;
}

/**
 * Ouderapp-knop, altijd een pil (docs/ux/ontwerpsysteem.md). Drie hoogtes: klein 36 (in een tikdoel
 * van 44), normaal 44 en groot 52. De kiosk gebruikt BigButton uit ui-core; deze variant is
 * compacter en kent `type="submit"`.
 */
export function Button({
  children,
  name,
  onPress,
  type = 'button',
  variant = 'default',
  size = 'normal',
  icon,
  label,
  disabled,
  busy,
  block,
}: ButtonProps) {
  const isDisabled = Boolean(disabled || busy);
  const iconOnly = icon !== undefined && (children === undefined || children === null);
  const className = cx(
    styles.button,
    variant === 'primary' && styles.primary,
    variant === 'danger' && styles.danger,
    variant === 'ghost' && styles.ghost,
    variant === 'link' && styles.link,
    size === 'small' && styles.small,
    size === 'large' && styles.large,
    block && styles.block,
    iconOnly && styles.iconOnly,
    isDisabled && styles.disabled,
  );
  return (
    <button
      type={type}
      class={className}
      data-name={name}
      data-btn={size}
      disabled={isDisabled}
      aria-busy={busy ? 'true' : undefined}
      aria-label={label}
      title={iconOnly ? label : undefined}
      onClick={(e) => {
        if (isDisabled) return;
        if (onPress) {
          if (type === 'button') e.preventDefault();
          onPress();
        }
      }}
    >
      {busy ? (
        <span class={styles.spinner} aria-hidden="true" />
      ) : icon ? (
        <Icon id={icon} size={size === 'small' ? 16 : 20} class={styles.icon} />
      ) : null}
      {children}
    </button>
  );
}
